import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { patientsApi } from "../../../shared/api/patients";
import { apiError } from "../../../shared/api/client";
import { formatDate } from "../../../shared/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

/**
 * Giấy tờ tuỳ thân — CCCD (nhiều số, 1 cái đang dùng = mới nhất, giữ lịch sử để
 * sau này đồng bộ HIS bệnh viện) + số thẻ BHYT. Nhân viên chỉnh trực tiếp ở đây.
 */
export function IdentifiersPanel({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const key = ["patients", patientId, "identifiers"] as const;
  const q = useQuery({ queryKey: key, queryFn: () => patientsApi.listIdentifiers(patientId) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const list = q.data ?? [];
  const cccds = list.filter((i) => i.identifierType === "CCCD");
  const bhyt = list.find((i) => i.identifierType === "BHYT");

  const [newCccd, setNewCccd] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [cccdErr, setCccdErr] = useState<string | null>(null);

  const addCccd = useMutation({
    mutationFn: () =>
      patientsApi.addCccd(patientId, { value: newCccd.trim(), issuedAt: issuedAt || null }),
    onSuccess: () => {
      setNewCccd("");
      setIssuedAt("");
      setCccdErr(null);
      invalidate();
    },
    onError: (e) => setCccdErr(apiError(e).detail),
  });

  function submitCccd() {
    if (!/^\d{12}$/.test(newCccd.trim())) {
      setCccdErr("CCCD phải có 12 chữ số.");
      return;
    }
    addCccd.mutate();
  }

  const [bhytVal, setBhytVal] = useState("");
  const [bhytSaved, setBhytSaved] = useState(false);
  useEffect(() => {
    setBhytVal(bhyt?.value ?? "");
  }, [bhyt?.value]);
  const saveBhyt = useMutation({
    mutationFn: () => patientsApi.setBhyt(patientId, bhytVal.trim()),
    onSuccess: () => {
      setBhytSaved(true);
      invalidate();
    },
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* CCCD */}
      <Card>
        <CardContent className="p-5">
          <h3 className="mb-1 text-base font-semibold text-foreground">CCCD</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            Một người có thể có nhiều CCCD; số mới nhất là số đang dùng, các số cũ được giữ làm lịch
            sử để đồng bộ với HIS bệnh viện.
          </p>

          {q.isLoading ? (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          ) : cccds.length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có CCCD.</p>
          ) : (
            <ul className="mb-3 divide-y rounded-md border">
              {cccds.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="font-mono text-sm">{c.value}</span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    {c.issuedAt && <span>Cấp {formatDate(c.issuedAt)}</span>}
                    {c.isPrimary && <Badge variant="success">Đang dùng</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="new-cccd">Thêm / đổi CCCD</Label>
              <Input
                id="new-cccd"
                inputMode="numeric"
                maxLength={12}
                placeholder="12 chữ số"
                value={newCccd}
                onChange={(e) => setNewCccd(e.target.value.replace(/\D/g, ""))}
                className="w-40"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="new-cccd-date">Ngày cấp</Label>
              <Input
                id="new-cccd-date"
                type="date"
                value={issuedAt}
                onChange={(e) => setIssuedAt(e.target.value)}
                className="w-auto"
              />
            </div>
            <Button type="button" disabled={addCccd.isPending} onClick={submitCccd}>
              {addCccd.isPending ? "Đang lưu…" : "Đặt làm CCCD hiện tại"}
            </Button>
          </div>
          {cccdErr && <p className="mt-2 text-sm text-destructive">{cccdErr}</p>}
        </CardContent>
      </Card>

      {/* BHYT */}
      <Card>
        <CardContent className="p-5">
          <h3 className="mb-1 text-base font-semibold text-foreground">Thẻ BHYT</h3>
          <p className="mb-3 text-sm text-muted-foreground">Số thẻ bảo hiểm y tế của bệnh nhân.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="bhyt">Số thẻ BHYT</Label>
              <Input
                id="bhyt"
                maxLength={20}
                value={bhytVal}
                onChange={(e) => {
                  setBhytVal(e.target.value);
                  setBhytSaved(false);
                }}
                placeholder="Để trống để xoá"
                className="w-56"
              />
            </div>
            <Button type="button" disabled={saveBhyt.isPending} onClick={() => saveBhyt.mutate()}>
              {saveBhyt.isPending ? "Đang lưu…" : "Lưu"}
            </Button>
            {bhytSaved && !saveBhyt.isPending && (
              <span className="pb-2 text-sm text-emerald-600">Đã lưu ✓</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
